import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/pro-solid-svg-icons'

import { Box, LoadingBubbles } from '@domain-group/suit-theme'

import type {
  AgencyModel,
  ArtworkModel,
  ArtworkPageModel,
  CampaignModel,
  OrderItemModel,
  PublicationBookingItemModel,
} from '@models/types'

import { confirmDelete } from '@functions/entity'

import { buildCreateArtworkUrl, buildEditArtworkUrl } from '@hooks/useArtworkBuilderPaths'
import useArtworkPagePreview, { UseArtworkPagePreviewPayload } from '@hooks/useArtworkPagePreview'
import useOrderItem from '@hooks/useOrderItem'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import type { UseOrderItemPayload } from '@hooks/useOrderItem'

import styles from './styles.module.scss'

type RenderArtworkLinkParams = {
  artwork: ArtworkModel,
  buildPreview: UseArtworkPagePreviewPayload['callbacks']['buildPreview'],
  createArtworkFn: UseOrderItemPayload['callbacks']['createArtwork'],
  deleting: boolean,
  orderItem: OrderItemModel,
  redirectUrl: string,
  selectedArtworkPage: ArtworkPageModel,
}

const renderArtworkLink = (params: RenderArtworkLinkParams) => {
  const {
    artwork,
    buildPreview,
    createArtworkFn,
    deleting,
    orderItem,
    redirectUrl,
    selectedArtworkPage,
  } = params

  // Existing Artwork - Template
  if (artwork?.artwork_type === 'template'){
    const editArtworkUrl = buildEditArtworkUrl({ artwork, redirectUrl })

    return (
      <>
        <a
          className={styles.root}
          onClick={() => buildPreview({ artworkPage: selectedArtworkPage })}
        >
          <FontAwesomeIcon icon={faRefresh} />
        </a>

        {/*
         // @ts-expect-error TS2322 */}
        <a href={editArtworkUrl} className={styles.root}>
          Edit Artwork
        </a>
      </>
    )
  }

  // Existing Artwork - File
  if (artwork?.artwork_type === 'file'){
    return (
      <a className={styles.root} onClick={() => console.log('TODO - confirmDeleteArtwork')}>
        {deleting ? <LoadingBubbles /> : 'Delete Artwork'}
      </a>
    )
  }

  // No Artwork - Create New
  const createArtworkUrl = buildCreateArtworkUrl({ orderItem, redirectUrl })

  return (
    <a className={styles.root} onClick={() => createArtworkFn(redirectUrl)}>
      Create Artwork
    </a>
  )
}

type RenderApproveArtworkParams = {
  approving: boolean,
  approveArtwork: VoidFunction,
}

const renderApproveArtworkButton = (params: RenderApproveArtworkParams) => {
  const { approving, approveArtwork } = params

  return (
    <a
      className={classnames(styles.root, styles.approve)}
      onClick={approveArtwork}
    >
      {approving ? <LoadingBubbles color="#ffffff" /> : 'Approve'}
    </a>
  )
}

type RenderUnapproveArtworkParams = {
  unapproveArtwork: VoidFunction,
  unapproving: boolean,
}

const renderUnapproveArtworkButton = (params: RenderUnapproveArtworkParams) => {
  const { unapproveArtwork, unapproving } = params

  return (
    <a onClick={unapproveArtwork} className={styles.root}>
      {unapproving ? <LoadingBubbles color="#ffffff" /> : 'Unapprove'}
    </a>
  )
}

type RenderRemoveItemButtonParams = {
  publicationBookingItem: PublicationBookingItemModel,
  removeOrDeletePublicationBookingItem: VoidFunction,
}

const renderRemoveItemButton = (params: RenderRemoveItemButtonParams) => {
  const { removeOrDeletePublicationBookingItem } = params

  return (
    <a
      onClick={() => confirmDelete({
        callbacks: {
          // @ts-expect-error TS2322
          deleteFn: removeOrDeletePublicationBookingItem,
        },
        strings: {
          actionString: 'Remove Item?',
          confirmString: 'Are you sure? This will remove the item from this page.',
          deleteString: 'remove',
          entityName: 'Item',
        },
      })}
      className={classnames(styles.root, styles.alert)}
    >
      Remove
    </a>
  )
}

interface ActionsProps {
  agency: AgencyModel,
  approving: boolean,
  artwork: ArtworkModel,
  callbacks: {
    approveArtwork: VoidFunction,
    buildPreview: UseArtworkPagePreviewPayload['callbacks']['buildPreview'],
    removeOrDeletePublicationBookingItem: VoidFunction,
    unapproveArtwork: VoidFunction,
  },
  campaign: CampaignModel,
  className: string,
  deleting: boolean,
  hasApprovedArtwork: boolean,
  isPending: boolean,
  isReleased: boolean,
  orderItem: OrderItemModel,
  publicationBookingItem: PublicationBookingItemModel,
  selectedArtworkPage: ArtworkPageModel,
  unapproving: boolean,
}

const Actions = (props: ActionsProps) => {
  const {
    agency,
    approving,
    artwork,
    callbacks: {
      approveArtwork,
      buildPreview,
      removeOrDeletePublicationBookingItem,
      unapproveArtwork,
    },
    campaign,
    className,
    deleting,
    hasApprovedArtwork,
    isPending,
    isReleased,
    orderItem,
    publicationBookingItem,
    publicationBookingItem: {
      pending_cancellation,
    },
    selectedArtworkPage,
    unapproving,
  } = props

  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])

  const orderItemPayload = useOrderItem(orderItem)
  const {
    callbacks: {
      createArtwork: createArtworkFn,
    },
  } = orderItemPayload

  if (pending_cancellation){
    return null
  }

  return (
    <Box
      className={className}
      fontSize="xsmall"
      position="absolute"
      right="0"
      top="0"
      width="auto"
      zIndex={2}
      style={{
        gap: 2,
      }}
    >
      {!hasApprovedArtwork && renderArtworkLink({
        artwork,
        buildPreview,
        createArtworkFn,
        deleting,
        orderItem,
        redirectUrl,
        selectedArtworkPage,
      })}
      {!!artwork.id && !hasApprovedArtwork && renderApproveArtworkButton({ approveArtwork, approving })}
      {!isReleased && hasApprovedArtwork && renderUnapproveArtworkButton({ unapproveArtwork, unapproving })}
      {isPending && renderRemoveItemButton({ publicationBookingItem, removeOrDeletePublicationBookingItem })}
    </Box>
  )
}

export default Actions
